import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { FluxMark, MASCOT_URL } from "../components/flux/Decor";
import { useAuth, API, storeSessionToken } from "../context/AuthContext";

const display = "font-display";

export default function Login() {
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const formatDetail = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
    return "Something went wrong. Please try again.";
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/matrix";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "signup" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = mode === "signup" ? { name, email, password } : { email, password };
      const res = await axios.post(url, payload);
      storeSessionToken(res.data.session_token);
      setUser(res.data);
      toast.success(mode === "signup" ? `Welcome aboard, ${res.data.name}!` : `Welcome back, ${res.data.name}!`);
      navigate("/matrix");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1729] flex items-center justify-center px-6 relative overflow-hidden" data-testid="login-page">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(245,158,11,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.3) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={MASCOT_URL} alt="Professor Flux" className="w-32 h-32 object-contain animate-float mb-2" />
          <Link to="/" className="flex items-center gap-2">
            <FluxMark className="w-7 h-7" />
            <span className={`${display} font-bold text-xl tracking-wider uppercase text-white`}>
              FLU<span className="text-[#f59e0b]">X</span> <span className="font-normal text-gray-300">CAPACITOR</span>
            </span>
          </Link>
          <p className="text-gray-300 text-sm mt-2">Step into the lab to bend some time.</p>
        </div>

        <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-6 shadow-2xl space-y-5">
          <button
            onClick={googleLogin}
            data-testid="google-login-btn"
            className={`w-full bg-white hover:bg-gray-100 text-gray-900 ${display} font-bold py-3 px-4 rounded-xl text-sm transition-colors duration-200 active:scale-[0.98] flex items-center justify-center gap-3`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">or email & password</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="bg-gray-950 p-1 rounded-xl border border-white/5 flex gap-1">
            <button
              type="button"
              onClick={() => setMode("signin")}
              data-testid="mode-signin-btn"
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${mode === "signin" ? "bg-[#f59e0b] text-gray-950" : "text-gray-400 hover:text-white"}`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              data-testid="mode-signup-btn"
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 ${mode === "signup" ? "bg-[#f59e0b] text-gray-950" : "text-gray-400 hover:text-white"}`}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={passwordLogin} className="space-y-3">
            {mode === "signup" && (
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                placeholder="Your name"
                className="w-full bg-gray-950 border border-white/5 rounded-lg p-3 text-sm text-gray-200 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200"
              />
            )}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              placeholder="you@email.com"
              className="w-full bg-gray-950 border border-white/5 rounded-lg p-3 text-sm text-gray-200 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              placeholder="Password"
              className="w-full bg-gray-950 border border-white/5 rounded-lg p-3 text-sm text-gray-200 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className={`w-full bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-60 text-gray-950 ${display} font-bold py-3 px-4 rounded-xl text-xs uppercase tracking-wider transition-colors duration-200 active:scale-[0.98] flex items-center justify-center gap-2`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {mode === "signup" ? "Create My Account" : "Enter The Lab"}
            </button>
          </form>

          <p className="text-[11px] text-gray-400 text-center leading-relaxed">
            New here? Just sign in with Google — <span className="text-[#2dd4bf] font-semibold">full access is free</span> while we're in open beta.
          </p>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          <Link to="/" className="hover:text-[#f59e0b] transition-colors duration-200">← Back to landing</Link>
        </p>
      </div>
    </div>
  );
}
